import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "@untitled-ui/icons-react";
import moment, { Moment } from "moment";
import classNames from "classnames";
import ReactCalendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import { Button, Spinner, Tooltip } from "@myhealth-raccoon/components";
import { useAsyncData } from "@myhealth-raccoon/services";
import { AutomedClinicAvailabilityTimeModel, AutomedDoctorAvailabilityTimeModel, AutomedDoctorAvailablilityModel, availabilityService, SiteBookingAvailabilityModel } from "src/services/availability";
import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react";
import { AvailabilityProps } from "./Availability";
import { useClinicSelector } from "src/hooks/useAsyncSelector";

type TimeslotButtonProps = {
    disabled?: boolean;
    time: Moment;
    selected?: Moment;
    onClick(): void;
    ts: Timeslot;
}

type Slots = { userId: number, duration: string, ts: AutomedDoctorAvailabilityTimeModel | AutomedClinicAvailabilityTimeModel }
type Timeslot = { time: string, slots: Slots[] }

const isTouchScreen = (window.matchMedia("(pointer: coarse)").matches);

const outputError = (e: Error) => {
    if (e.message === "No availabilities info were found") {
        return "We're sorry, but we have no appointments available for the selected appointment type."
    }
    return e.message || e.name;
}

function TimeslotButton({ disabled, time, selected, onClick, ts }: TimeslotButtonProps) {
    const button = <Button
        disabled={disabled}
        color={selected && selected?.isSame(time, 'minute') ? "primary" : "gray"}
        className={classNames('w-full relative !py-2 !px-0.5', {
            'hover:!text-primary hover:!border-primary hover:!bg-white': !selected
        })}
        onClick={onClick}>
        {time.format("h:mm a")}
        {ts.slots.length > 1 && <small className="text-xs text-gray-400 font-normal absolute right-2 top-1">{' '}{ts.slots.length}</small>}
    </Button>;
    if (isTouchScreen) {
        return button;
    }
    return <Tooltip
        className={classNames({ "pointer-events-none": disabled })}
        attachmentPoint="right"
        tooltipContent={<>{time.format("ddd, D MMM")}<br />{time.format("h:mm a")} ({ts.slots[0].duration} mins)</>}
    >
        {button}
    </Tooltip>
}

export function AvailabilityCalendar({
    selected, onSelectTimeSlot, appointmentType, clinicId, doctorId, disabled, firstAvailableDate, errorElement, futureApptWeeks
}: AvailabilityProps) {
    const [startDate, setStartDate] = useState(() => moment().startOf("date"));
    const endDate = useMemo(() => moment(startDate).add(3, "day"), [startDate]);
    const lastAvailableDay = useMemo(() => futureApptWeeks ? moment().add(futureApptWeeks, "weeks") : moment().add(30, "days"), [futureApptWeeks]);
    const [timeSlotsDelta, setTimeSlotsDelta] = useState(0);

    const { data: site } = useClinicSelector();

    const { data: availabilityData, ...timeslotsRequest } = useAsyncData({
        service: availabilityService,
        method: availabilityService.GetBookingTimeslot,
        defaultValue: {},
        suppressToast: true,
        selector: useCallback((data: SiteBookingAvailabilityModel[] | AutomedDoctorAvailablilityModel) => {
            let obj = {} as Record<string, Timeslot[]>;

            if (Array.isArray(data)) {
                obj = data.reduce(
                    (obj, { user, timeSlots }) => {
                        for (const [date, slots] of Object.entries(timeSlots)) {
                            if (obj[date]) {
                                for (const ts of slots) {
                                    const existingSlot = obj[date].find(({ time }) => time === ts.t);

                                    if (existingSlot) {
                                        existingSlot.slots.push({ userId: user.userId, duration: ts.d, ts });
                                    } else {
                                        obj[date].push({ time: ts.t, slots: [{ userId: user.userId, duration: ts.d, ts }] });
                                        obj[date] = obj[date].sort(({ time: a }, { time: b }) => moment(a , "HH:mm").isBefore(moment(b, "HH:mm")) ? -1 : 1);
                                    }
                                }
                            } else {
                                obj[date] = slots.map((ts) => ({
                                    time: ts.t, slots: [{ userId: user.userId, duration: ts.d, ts }]
                                }))
                            }
                        }
                        return obj;
                    }, obj);
            } else {
                for (const [date, slots] of Object.entries(data.timeslot)) {
                    obj[date] = slots.map((ts) => ({ time: ts.time, slots: [{ userId: Number(doctorId!), duration: ts.duration, ts }] }));
                }
            }
            return obj;
        }, [doctorId])
    },
        clinicId,
        appointmentType.appointmentTypeId,
        doctorId,
        useMemo(() => ({
            startDate: startDate.toDate(),
            endDate: endDate.toDate(),
            firstAvailableOrCalendar: false
        }), [startDate, endDate]));

    const timeSlotsByDay = useMemo(() => {
        return Array(4).fill(0).map((_, i) => {
            const date = moment(startDate).add(i, "day");
            if (availabilityData[date.format("YYYY-MM-DD")]) {
                return { date, times: availabilityData[date.format("YYYY-MM-DD")] }
            } else {
                return { date, times: [] }
            }
        });
    }, [availabilityData, startDate]);

    // NOTE: This code is used to get number of slots per day for Calendar Busy Status
    //      For now, this is commented out until we discuss if we need this.
    // const slotCountByDay :{[key: string]: number}= useMemo(() => {
    //     let obj:{[key: string]: number} = {};
    //     Object.keys(availabilityData).map((day)=>{
    //         obj[day] = 0;
    //         availabilityData[day].map((slot) =>{
    //             obj[day] += slot.slots.length;
    //         });
    //     });
    //     return obj;
    // }, [availabilityData, startDate]);

    const longestTimeSlots = useMemo(() => {
        const sorted = [...timeSlotsByDay]
            .sort(({ times: a }, { times: b }) => (b?.length ?? 0) - (a?.length ?? 0))
        return sorted[0];
    }, [timeSlotsByDay]);
    const [skipTimeSlots, setSkipTimeSlots] = useState(0);
    useEffect(() => {
        setSkipTimeSlots(0);
        setTimeSlotsDelta(0);
    }, [availabilityData]);

    const onClickUp = useCallback(() => {
        requestAnimationFrame(() => {
            setSkipTimeSlots(curr => curr - 4);
        });
        setTimeSlotsDelta(-4);
    }, []);

    const onClickDown = useCallback(() => {
        requestAnimationFrame(() => {
            setSkipTimeSlots(curr => curr + 4);
        });
        setTimeSlotsDelta(4);
    }, []);

    return <>
        <div className="p-2.5 border-t border-b border-gray-200 flex flex-row">
            <h4 className="p-1.5 text-gray-900 text-lg ">
                <strong className="font-semibold">
                    {startDate.isSame(endDate, "month")
                        ? startDate.format("MMMM")
                        : `${startDate.format("MMM")} - ${endDate.format("MMM")}`}
                </strong>
                {' '}
                {startDate.isSame(endDate, "year")
                    ? startDate.format("YYYY")
                    : `${startDate.format("YYYY")}/${endDate.format("YYYY")}`}
            </h4>

            <Popover as={Fragment}>
                <div className="ml-auto border border-gray-300 rounded shadow-sm">
                    <PopoverButton disabled={disabled} className="transition-colors data-[open]:text-primary hover:bg-gray-50 text-gray-500 disabled:text-gray-300 disabled:pointer-events-none bg-white border-r border-inherit p-2.5 rounded-s"><Calendar className="h-5 w-5" /></PopoverButton>
                    <button
                        onClick={() => setStartDate(curr => moment(curr).subtract(4, "days"))}
                        className="transition-colors hover:bg-gray-50 text-gray-500 disabled:text-gray-300 disabled:pointer-events-none bg-white border-inherit p-2.5" disabled={disabled || startDate.isSameOrBefore(moment(), 'date')}
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                        onClick={() => setStartDate(curr => moment(curr).add(4, "days"))}
                        className="transition-colors hover:bg-gray-50 text-gray-500 disabled:text-gray-300 disabled:pointer-events-none bg-white border-l border-inherit p-2.5 rounded-e"
                        disabled={disabled || endDate.isSameOrAfter(lastAvailableDay, "date")}
                    >
                        <ChevronRight className="h-5 w-5" />
                    </button>
                </div>
                <PopoverPanel
                    transition
                    anchor="bottom end"
                    className="z-50 [--anchor-gap:1px] transition duration-200 ease-in-out border border-gray-100 shadow-xl rounded-lg">
                    {({ close }) => <div className="bg-white max-w-xs px-4 py-4 ">
                        <ReactCalendar
                            formatShortWeekday={(_, date) => moment(date).format("dd")}
                            onChange={(value => {
                                let date: Date | null;
                                if (Array.isArray(value)) {
                                    date = value[0];
                                } else {
                                    date = value;
                                }
                                if (date) {
                                    setStartDate(moment(date));
                                    close();
                                }
                            })}
                            value={startDate.toDate()}
                            minDate={new Date()}
                            maxDate={lastAvailableDay?.toDate()}
                            minDetail="month"
                            returnValue="start"
                            // NOTE: This code is used to show Calendar Busy Status
                            //      For now, this is commented out until we discuss if we need this.
                            // tileClassName={({ date }) => {
                            //     const mDate = moment(date);
                            //     const fDate =mDate.format("YYYY-MM-DD");
                            //     return classNames("border", {
                            //         "after:bg-success": slotCountByDay[fDate] >= 5 && mDate.isSameOrAfter(moment(), "date") && mDate.isSameOrBefore(lastAvailableDay, "date"), 
                            //         "after:bg-warning": slotCountByDay[fDate] < 5  && mDate.isSameOrAfter(moment(), "date") && mDate.isSameOrBefore(lastAvailableDay, "date"), 
                            //         "after:bg-error": !slotCountByDay[fDate] && mDate.isSameOrAfter(moment(), "date") && mDate.isSameOrBefore(lastAvailableDay, "date")
                            //     });
                            // }}
                            prevLabel={<ChevronLeft className="w-5 h-5 m-auto" />}
                            nextLabel={<ChevronRight className="w-5 h-5 m-auto" />}
                        />
                    </div>}
                </PopoverPanel>
            </Popover>
        </div>

        {/* 
        green - available slots and not busy
        red - indicates not available or the centre is closed
        YELLOW - limited slots available 
        */}
        <div className="flex flex-row border-b border-gray-200 justify-around px-4 ">
            {timeSlotsByDay.map(({ date, times }) => {
                return (
                    <div className={classNames("text-sm text-center font-base flex-1 p-2 relative",
                        "after:content-[''] after:block after:absolute after:inset-x-0 after:mx-auto after:bottom-0 after:w-4 after:h-[3px] after:rounded-sm even:bg-gray-50",
                        {
                            "after:bg-success": times.length > 4,
                            "after:bg-error text-gray-400": !timeslotsRequest.isLoading && !times.length,
                            "after:bg-warning": times.length && times.length <= 4
                        }
                    )} key={date.toDate().getTime().toString()}>
                        {date.format("ddd")}<br />
                        <strong className="font-black">{date.format("D MMM")}</strong>
                    </div>
                )
            })}
        </div>
        <div className="flex flex-col p-4">
            <button disabled={disabled || skipTimeSlots <= 0} onClick={onClickUp} className="w-full bg-primary-25 shadow-sm rounded p-2 flex items-center justify-center text-primary disabled:text-primary-200 disabled:pointer-events-none">
                <ChevronUp className="w-5 h-5" />
            </button>
            <div className="min-h-44 flex flex-col items-center justify-center">
                {errorElement
                    ? <div className="p-8 text-center space-y-1">
                        {errorElement}
                    </div>
                    : timeslotsRequest.isLoading
                        ? <div className="p-8 text-center space-y-1">
                            <Spinner className="flex justify-center" />
                            <p>We're fetching the available appointment times. This may take a moment...</p>
                        </div>
                        : timeslotsRequest.error
                            ? <div className="p-8 text-center space-y-1">
                                <p><strong>{outputError(timeslotsRequest.error)}</strong></p>
                                <p>Please contact the centre to make an appointment on{' '}
                                    <a className="text-primary" href={`tel:${site?.phoneNumber}`} >{site?.phoneNumber}</a>.</p>
                            </div>
                            : timeSlotsByDay.find(({ times }) => !!times?.length)
                                ? <div className="flex-1 w-full grid grid-cols-4 animate-fade-in">
                                    {timeSlotsByDay.map(({ date, times }) => {
                                        let viewBlankSlots = 4;
                                        return <div key={date.toISOString()} className="grid grid-rows-4 px-2 py-4 gap-y-2.5 even:bg-gray-50 !px-1">
                                            {times?.map((ts, i) => {
                                                const show = !(i < skipTimeSlots || i >= skipTimeSlots + 4);
                                                if (show) { viewBlankSlots--; }
                                                const time = moment(`${date.format("YYYY-MM-DD")} ${ts.time}`);
                                                return <Transition show={show}
                                                    as="div"
                                                    key={time.toDate().toString()}
                                                    leave="absolute hidden"
                                                    enter="transition"
                                                    enterFrom={classNames("opacity-0 invisible", {
                                                        "translate-y-full": timeSlotsDelta >= 0,
                                                        "-translate-y-full": timeSlotsDelta < 0
                                                    })}
                                                >
                                                    <TimeslotButton
                                                        onClick={() => onSelectTimeSlot({
                                                            date: time.toDate(),
                                                            doctorId: (doctorId !== undefined
                                                                ? doctorId
                                                                : ts.slots.map(({ userId }) => userId)) as number & number[],
                                                            appointmentTypeId: appointmentType.appointmentTypeId,
                                                            clinicId,
                                                            timeSlot: ts.slots[0].ts as AutomedDoctorAvailabilityTimeModel,
                                                            timeSlots: ts.slots.map(s => s.ts) as AutomedClinicAvailabilityTimeModel[]
                                                        })}
                                                        disabled={disabled}
                                                        selected={selected}
                                                        time={time}
                                                        ts={ts}
                                                    />
                                                </Transition>
                                            })}
                                            {viewBlankSlots > 0 && Array.from({ length: viewBlankSlots }, (_, i) => (
                                                <div className="w-full h-full" key={i}>
                                                    <div className="flex justify-center pt-2 text-[#D1D5DC]" >-- --</div>
                                                </div>
                                            ))}
                                        </div>
                                    })}
                                </div>
                                : (firstAvailableDate && firstAvailableDate != '')
                                    ? <div className="2 text-center text-gray-500 text-sm">
                                        <p className="text-gray-400 mb-4">There are no appointments available for the selected dates.</p>
                                        <Button
                                            onClick={() => { setStartDate(moment(firstAvailableDate)) }}
                                            disabled={timeslotsRequest.isLoading || disabled}>
                                            Jump to next available date:
                                            {' '}
                                            {moment(`${firstAvailableDate}`).format("ddd, DD MMM")}
                                        </Button>
                                    </div>
                                    : (doctorId)
                                        ? <div className="p-8 text-center text-gray-500 text-sm">
                                            <p><strong className="text-gray-800 font-bold">There are no appointments available for the selected provider.</strong></p>
                                            <p>Please select another provider or contact the centre to make an appointment on
                                                {' '}
                                                <a className="text-primary" href={`tel:${site?.phoneNumber}`} >{site?.phoneNumber}</a>.</p>
                                        </div>
                                        : <div className="p-8 text-center text-gray-500 text-sm">
                                            <p><strong className="text-gray-800 font-bold">We’re sorry, but we have no appointments available for the selected appointment type.</strong></p>
                                            <p>Please contact the centre to make an appointment on
                                                {' '}
                                                <a className="text-primary" href={`tel:${site?.phoneNumber}`} >{site?.phoneNumber}</a>.</p>
                                        </div>
                }
            </div>
            <button className="w-full bg-primary-25 font-semibold shadow-sm rounded p-2 flex items-center justify-center text-primary gap-2 disabled:text-primary-200 disabled:pointer-events-none"
                disabled={disabled || (longestTimeSlots?.times?.length ?? 0) - 4 <= skipTimeSlots}
                onClick={onClickDown}
            >
                More Times
                <ChevronDown className="w-5 h-5" />
            </button>
        </div>
    </>
}